import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import Home from "./pages/Home";

const About = React.lazy(() => import("./pages/About"));
const Solutions = React.lazy(() => import("./pages/Solutions"));
const Projects = React.lazy(() => import("./pages/Projects"));
const ProjectDetails = React.lazy(() => import("./pages/ProjectDetails"));
const Sustainability = React.lazy(() => import("./pages/Sustainability"));
const Insights = React.lazy(() => import("./pages/Insights"));
const Knowledge = React.lazy(() => import("./pages/Knowledge"));
const Resources = React.lazy(() => import("./pages/knowledge/Resources"));
const Learn = React.lazy(() => import("./pages/knowledge/Learn"));
const Perspectives = React.lazy(() => import("./pages/knowledge/Perspectives"));
const ToolsAndData = React.lazy(() => import("./pages/knowledge/ToolsAndData"));
const ResourceDetail = React.lazy(() => import("./pages/knowledge/ResourceDetail"));
const Company = React.lazy(() => import("./pages/Company"));
const AboutKisan = React.lazy(() => import("./pages/company/AboutKisan"));
const Leadership = React.lazy(() => import("./pages/company/Leadership"));
const GlobalPresence = React.lazy(() => import("./pages/company/GlobalPresence"));
const Newsroom = React.lazy(() => import("./pages/company/Newsroom"));
const Careers = React.lazy(() => import("./pages/company/Careers"));
const Divisions = React.lazy(() => import("./pages/Divisions"));
const Industries = React.lazy(() => import("./pages/Industries"));
const Contact = React.lazy(() => import("./pages/Contact"));
const ThankYou = React.lazy(() => import("./pages/ThankYou"));
const FarmManagementServices = React.lazy(() => import("./pages/solutions/FarmManagementServices"));
const OrchardAndPlantationDevelopment = React.lazy(() => import("./pages/solutions/OrchardAndPlantationDevelopment"));
const OrganicFarmingAndSoilHealth = React.lazy(() => import("./pages/solutions/OrganicFarmingAndSoilHealth"));
const WaterAndIrrigationSystems = React.lazy(() => import("./pages/solutions/WaterAndIrrigationSystems"));
const SacredAndHeritageLandscapes = React.lazy(() => import("./pages/solutions/SacredAndHeritageLandscapes"));
const MasterPlanningAndDesign = React.lazy(() => import("./pages/solutions/MasterPlanningAndDesign"));
const LandAssessmentAndPlanning = React.lazy(() => import("./pages/solutions/LandAssessmentAndPlanning"));
const FarmSecurityAndSurveillance = React.lazy(() => import("./pages/solutions/FarmSecurityAndSurveillance"));
const EcologicalRestoration = React.lazy(() => import("./pages/solutions/EcologicalRestoration"));

// Division Subpages
const Plantiq = React.lazy(() => import("./pages/divisions/Plantiq"));
const Agriq = React.lazy(() => import("./pages/divisions/Agriq"));
const Vigoriq = React.lazy(() => import("./pages/divisions/Vigoriq"));
const Irriq = React.lazy(() => import("./pages/divisions/Irriq"));
const Secure = React.lazy(() => import("./pages/divisions/Secure"));
const GreenHabitat360 = React.lazy(() => import("./pages/divisions/GreenHabitat360"));
const Farm360 = React.lazy(() => import("./pages/divisions/Farm360"));
const Organiq = React.lazy(() => import("./pages/divisions/Organiq"));
const Vedvan = React.lazy(() => import("./pages/divisions/Vedvan"));

// Industry Subpages
const Agriculture = React.lazy(() => import("./pages/industries/Agriculture"));
const Institutions = React.lazy(() => import("./pages/industries/Institutions"));
const Government = React.lazy(() => import("./pages/industries/Government"));
const RealEstate = React.lazy(() => import("./pages/industries/RealEstate"));
const IndustryCSR = React.lazy(() => import("./pages/industries/IndustryCSR"));

// Technology Pages
const Technology = React.lazy(() => import("./pages/Technology"));
const DataInsights = React.lazy(() => import("./pages/technology/DataInsights"));
const DesignSimulate = React.lazy(() => import("./pages/technology/DesignSimulate"));
const OperateAutomate = React.lazy(() => import("./pages/technology/OperateAutomate"));
const MonitorImprove = React.lazy(() => import("./pages/technology/MonitorImprove"));
const CloudPlatform = React.lazy(() => import("./pages/technology/CloudPlatform"));
const MobileFieldTech = React.lazy(() => import("./pages/technology/MobileFieldTech"));
const SustainabilityTech = React.lazy(() => import("./pages/technology/SustainabilityTech"));
const Cybersecurity = React.lazy(() => import("./pages/technology/Cybersecurity"));

const CanonicalTag = () => {
   const location = useLocation();
 
   let pathname = location.pathname;
   if (pathname !== '/' && pathname.endsWith('/')) {
       pathname = pathname.slice(0, -1);
   }
   const canonicalUrl = `https://kisanplant.technology${pathname}`;
 
   return (
     <Helmet>
       <link rel="canonical" href={canonicalUrl} />
     </Helmet>
   );
};

const ScrollToHashElement = () => {
   const { pathname, hash } = useLocation();
 
   useEffect(() => {
     if (hash) {
       let attempts = 0;
       const interval = setInterval(() => {
         const id = hash.replace('#', '');
         const element = document.getElementById(id);
         if (element) {
           const yOffset = -100;
           const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
           window.scrollTo({ top: y, behavior: 'smooth' });
           clearInterval(interval);
         }
         attempts++;
         if (attempts >= 20) {
           clearInterval(interval);
         }
       }, 100);
       
       return () => clearInterval(interval);
     }
   }, [pathname, hash]);
 
   return null;
};

const AppRouter = () => {
   return (
      <BrowserRouter>
         <CanonicalTag />
         <ScrollToHashElement />
         <Suspense fallback={<div className="flex h-screen w-screen items-center justify-center bg-white"><div className="w-12 h-12 border-4 border-[#339144]/20 border-t-[#339144] rounded-full animate-spin"></div></div>}>
            <Routes>
               <Route path="/" element={<Home />} />
               <Route path="/about" element={<About />} />
               <Route path="/divisions" element={<Divisions />} />
               <Route path="/industries" element={<Industries />} />
               <Route path="/divisions/plantiq" element={<Plantiq />} />
               <Route path="/divisions/agriq" element={<Agriq />} />
               <Route path="/divisions/vigoriq" element={<Vigoriq />} />
               <Route path="/divisions/irriq" element={<Irriq />} />
               <Route path="/divisions/secure" element={<Secure />} />
               <Route path="/divisions/greenhabitat-360" element={<GreenHabitat360 />} />
               <Route path="/divisions/farm360" element={<Farm360 />} />
               <Route path="/divisions/organiq" element={<Organiq />} />
               <Route path="/divisions/vedvan" element={<Vedvan />} />

               {/* Solutions Routes */}
               <Route path="/solutions/farm-management-services" element={<FarmManagementServices />} />
               <Route path="/solutions/orchard-and-plantation-development" element={<OrchardAndPlantationDevelopment />} />
               <Route path="/solutions/organic-farming-and-soil-health" element={<OrganicFarmingAndSoilHealth />} />
               <Route path="/solutions/water-and-irrigation-systems" element={<WaterAndIrrigationSystems />} />
               <Route path="/solutions/sacred-and-heritage-landscapes" element={<SacredAndHeritageLandscapes />} />
               <Route path="/solutions/master-planning-and-design" element={<MasterPlanningAndDesign />} />
               <Route path="/solutions/land-assessment-and-planning" element={<LandAssessmentAndPlanning />} />
               <Route path="/solutions/farm-security-and-surveillance" element={<FarmSecurityAndSurveillance />} />
               <Route path="/solutions/ecological-restoration" element={<EcologicalRestoration />} />
               <Route path="/solutions" element={<Solutions />} />
               <Route path="/projects" element={<Projects />} />
               <Route path="/projects/:id" element={<ProjectDetails />} />
               <Route path="/sustainability" element={<Sustainability />} />
               <Route path="/insights" element={<Insights />} />
               <Route path="/knowledge" element={<Knowledge />} />
               <Route path="/knowledge/resources" element={<Resources />} />
               <Route path="/knowledge/learn" element={<Learn />} />
               <Route path="/knowledge/perspectives" element={<Perspectives />} />
               <Route path="/knowledge/tools-data" element={<ToolsAndData />} />
               <Route path="/knowledge/resources/:slug" element={<ResourceDetail />} />
               <Route path="/company" element={<Company />} />
               <Route path="/company/about" element={<AboutKisan />} />
               <Route path="/company/leadership" element={<Leadership />} />
               <Route path="/company/global" element={<GlobalPresence />} />
               <Route path="/company/newsroom" element={<Newsroom />} />
               <Route path="/careers" element={<Careers />} />
               <Route path="/contact" element={<Contact />} />
               <Route path="/thank-you" element={<ThankYou />} />

               {/* Industry Subpages */}
               <Route path="/industries/agriculture" element={<Agriculture />} />
               <Route path="/industries/agriculture/:subCategory" element={<Agriculture />} />
               <Route path="/industries/institutions" element={<Institutions />} />
               <Route path="/industries/institutions/:subCategory" element={<Institutions />} />
               <Route path="/industries/government" element={<Government />} />
               <Route path="/industries/government/:subCategory" element={<Government />} />
               <Route path="/industries/real-estate" element={<RealEstate />} />
               <Route path="/industries/real-estate/:subCategory" element={<RealEstate />} />
               <Route path="/industries/industry-csr" element={<IndustryCSR />} />
               <Route path="/industries/industry-csr/:subCategory" element={<IndustryCSR />} />

               {/* Technology Pages */}
               <Route path="/technology" element={<Technology />} />
               <Route path="/technology/data" element={<DataInsights />} />
               <Route path="/technology/data/:subCategory" element={<DataInsights />} />
               <Route path="/technology/design" element={<DesignSimulate />} />
               <Route path="/technology/design/:subCategory" element={<DesignSimulate />} />
               <Route path="/technology/operate" element={<OperateAutomate />} />
               <Route path="/technology/operate/:subCategory" element={<OperateAutomate />} />
               <Route path="/technology/monitor" element={<MonitorImprove />} />
               <Route path="/technology/monitor/:subCategory" element={<MonitorImprove />} />
               <Route path="/technology/cloud" element={<CloudPlatform />} />
               <Route path="/technology/cloud/:subCategory" element={<CloudPlatform />} />
               <Route path="/technology/mobile" element={<MobileFieldTech />} />
               <Route path="/technology/mobile/:subCategory" element={<MobileFieldTech />} />
               <Route path="/technology/sustainability" element={<SustainabilityTech />} />
               <Route path="/technology/sustainability/:subCategory" element={<SustainabilityTech />} />
               <Route path="/technology/cybersecurity" element={<Cybersecurity />} />
               <Route path="/technology/cybersecurity/:subCategory" element={<Cybersecurity />} />
            </Routes>
         </Suspense>
      </BrowserRouter>
   );
};

export default AppRouter;